/**
 * Artwork sizing/placement geometry for clothing mockups — the browser-side
 * mirror of app/Services/MockupGeometry.php. Converts between a pose's three
 * stored calibration numbers (ppi / horizontal_center / collar_offset) and the
 * native-px max-print rectangle, so the Mockup Editor can drag a box and
 * back-solve the numbers, or render the box from the numbers.
 *
 * A max print occupies:  width = maxWidthIn * ppi  centered on horizontal_center,
 * height = maxHeightIn * ppi, top at collar_offset + drop*ppi, where drop is the
 * inches-below-collar floor for the blank at that location — Monster's
 * confirmed per-blank offset where there is one, else the legacy per-location
 * constant (front 2, back 3, pocket 2). See dropInches.
 */

export const NATIVE_PX = 2400;

export const LOCATION_DROP_INCHES = { front: 2, back: 3, pocket: 2, collar: 2 };

/** The catalog_blanks column holding Monster's confirmed offset per location. */
const DTG_OFFSET_COLUMN = {
  front: "dtg_front_offset_inches",
  back: "dtg_back_offset_inches",
  pocket: "dtg_pocket_offset_inches",
};

/**
 * The drop in force for a blank at a location — mirrors
 * MockupGeometry::dropInches. The constants above are Monster's MENS row;
 * their guidelines put womens and fleece at 1.5in, tanks and infant at 1in,
 * and a youth BACK at 0.5in. A blank confirmed against that table carries its
 * own number and everything else falls back to the constant.
 *
 * The editor MUST use this rather than the constant directly: numbersFromBox
 * back-solves collar_offset against the drop, so a box dragged under one drop
 * and rendered under another writes a number the renderer disagrees with — the
 * silent divergence that shipped 6710's clipped labels across 21 poses.
 *
 * 'collar' has no column: back-neck is its own Monster row and prints at 2in.
 */
export function dropInches(blank, location = "front") {
  const column = DTG_OFFSET_COLUMN[location];
  const stored = column ? blank?.[column] : null;
  if (stored !== null && stored !== undefined && stored !== "") return Number(stored);
  return LOCATION_DROP_INCHES[location] ?? 2;
}

/**
 * Monster's maximum print area for the BN (back neck) location — "Back neck
 * print 10x12in, 2in from collar" in their DTG guidelines. It is Monster-wide,
 * not per-blank, so it is the collar frame on every back pose: what the
 * operator calibrates against is what a maximum back-neck print occupies.
 * Keep in step with Submission::BN_MAX_PRINT_INCHES.
 */
export const BN_MAX_PRINT_INCHES = { width: 10, height: 12 };

/** DB column stem for a location (front/back share front_*; pocket and a back pose's collar share pocket_*). */
export function columnLocation(location) {
  return location === "pocket" || location === "collar" ? "pocket" : "front";
}

/** Max print footprint in inches for a location — the blank's own maxima, or Monster's BN area for the collar. */
export function maxBoxInches(blank, location) {
  if (location === "collar") return { ...BN_MAX_PRINT_INCHES };
  const stem = location === "pocket" ? "pocket" : location === "back" ? "back" : "front";
  return {
    width: Number(blank[`${stem}_max_width`]),
    height: Number(blank[`${stem}_max_height`]),
  };
}

/**
 * The platen the mockup fits a DTG file into at a location, in inches — the
 * blank's own pick for that location, else its confirmed row's (served as
 * blank.dtg.pallets.front / .back). Null when none applies (an unrowed
 * blank with no pick; the pocket and collar). The platen is returned at
 * its own size, larger than the print box or not, so the editor can always
 * show what Monster prints inside; `fits` says the location's whole max box
 * sits within it (mens: 15×18 around an 11×14), i.e. nothing is ever shrunk.
 */
export function palletBoxInches(blank, location) {
  if (!blank || (location !== "front" && location !== "back")) return null;
  const p = blank.dtg?.pallets?.[location];
  const width = Number(p?.width);
  const height = Number(p?.height);
  if (!(width > 0) || !(height > 0)) return null;
  const max = maxBoxInches(blank, location);
  return { width, height, fits: width >= max.width && height >= max.height };
}

/** Forward: three numbers -> native-px rectangle of the max print. */
export function boxFromNumbers(ppi, horizontalCenter, collarOffset, maxBox, location = "front", dropIn = null) {
  const drop = dropIn ?? LOCATION_DROP_INCHES[location] ?? 2;
  const width = maxBox.width * ppi;
  const height = maxBox.height * ppi;
  const left = horizontalCenter - width / 2;
  const top = collarOffset + drop * ppi;
  return { left, top, width, height, right: left + width, bottom: top + height, centerX: horizontalCenter };
}

/** Inverse: native-px rectangle (left/top/width) -> three numbers (rounded). */
export function numbersFromBox(left, top, width, maxBox, location = "front", dropIn = null) {
  const drop = dropIn ?? LOCATION_DROP_INCHES[location] ?? 2;
  const ppi = width / maxBox.width;
  return {
    ppi: Math.round(ppi),
    horizontal_center: Math.round(left + width / 2),
    collar_offset: Math.round(top - drop * ppi),
  };
}

/**
 * Neck-label defaults — keep in step with MockupGeometry.php. The label is
 * placed from the pose's own calibration rather than baked into a per-pose
 * asset, so these are the only numbers the editor needs to draw it.
 */
export const TAG_WIDTH_INCHES = 2.0;

/**
 * Inches below the TOP EDGE of the neck opening where the label's top sits —
 * the anchor for any pose with a measured opening. Physical, not per-template:
 * the opening's top edge is the back-neck binding the label is sewn below.
 */
export const TAG_NECK_DROP_INCHES = 0.95;

/**
 * Height/width of cb-neck-label.png (1000x1192). The renderer resizes the
 * label by width and lets height follow, so this is only needed to know how
 * much of it an opening shows — PHP reads it off the file
 * (MockupGeometry::tagAspect); the browser has no file to read.
 */
export const TAG_ASPECT = 1192 / 1000;

/** FALLBACK anchor for poses with no measured opening — see MockupGeometry.php. */
export const TAG_COLLAR_RISE_INCHES = 2.7;

/** Native-px rectangle the label occupies, anchored to the neck opening. `aspect` is height/width. */
export function tagBoxFromNeckBox(ppi, neckBox, widthInches, offsetX = 0, offsetY = 0, aspect = null) {
  const width = widthInches * ppi;
  const centerX = neckBox.left + neckBox.w / 2 + offsetX;
  const top = neckBox.top + TAG_NECK_DROP_INCHES * ppi + offsetY;
  return { left: centerX - width / 2, top, width, height: aspect === null ? null : width * aspect, centerX };
}

/** FALLBACK: native-px rectangle the neck label occupies. `aspect` is height/width. */
export function tagBoxFromNumbers(ppi, horizontalCenter, collarOffset, widthInches, offsetX = 0, offsetY = 0, aspect = null) {
  const width = widthInches * ppi;
  const centerX = horizontalCenter + offsetX;
  const top = collarOffset - TAG_COLLAR_RISE_INCHES * ppi + offsetY;
  return { left: centerX - width / 2, top, width, height: aspect === null ? null : width * aspect, centerX };
}

/**
 * The pose's neck opening as a native-px bbox, or null. Mirrors
 * MockupGeometry::neckBoxFromPose — measured mask first (neck_box_*, a
 * left/top corner), then a hand-drawn ellipse (neck_clip_*, a CENTRE).
 */
export function neckBoxFromPose(mockup) {
  if (Number(mockup?.neck_box_w) > 0 && Number(mockup?.neck_box_h) > 0) {
    return {
      left: Number(mockup.neck_box_left),
      top: Number(mockup.neck_box_top),
      w: Number(mockup.neck_box_w),
      h: Number(mockup.neck_box_h),
    };
  }
  if (Number(mockup?.neck_clip_w) > 0 && Number(mockup?.neck_clip_h) > 0) {
    return neckBoxFromEllipse(mockup.neck_clip_x, mockup.neck_clip_y, mockup.neck_clip_w, mockup.neck_clip_h);
  }
  return null;
}

/** An editor ellipse (centre + size) as the bbox the label anchors to. */
export function neckBoxFromEllipse(cx, cy, w, h) {
  return { left: Math.round(cx - w / 2), top: Math.round(cy - h / 2), w: Number(w), h: Number(h) };
}

/**
 * The label rectangle for a pose straight from its row, or null when the pose
 * has no scaled tag. Mirrors MockupGeometry::tagBoxFromPose — anchored to the
 * pose's own neck opening when it has one, collar offset when it doesn't.
 */
export function tagBoxFromPose(mockup, aspect = TAG_ASPECT) {
  if (!mockup?.tag_image || mockup.tag_width_inches === null || mockup.tag_width_inches === undefined) return null;
  const ppi = Number(mockup.ppi);
  const width = Number(mockup.tag_width_inches);
  const ox = Number(mockup.tag_offset_x) || 0;
  const oy = Number(mockup.tag_offset_y) || 0;
  const neck = neckBoxFromPose(mockup);
  return neck ? tagBoxFromNeckBox(ppi, neck, width, ox, oy, aspect) : tagBoxFromNumbers(ppi, Number(mockup.front_horizontal_center), Number(mockup.front_collar_offset), width, ox, oy, aspect);
}

/**
 * Fraction of the label's height that falls inside the opening, 0..1. The
 * renderer keeps only what lands inside, so 0 means the pose renders with no
 * label at all. Mirrors MockupGeometry::tagVisibleFraction.
 */
export function tagVisibleFraction(tagBox, neckBox) {
  if (!neckBox) return 1;
  const height = Number(tagBox?.height) || 0;
  if (height <= 0) return 0;
  const overlap = Math.min(tagBox.top + height, neckBox.top + neckBox.h) - Math.max(tagBox.top, neckBox.top);
  return Math.max(0, Math.min(1, overlap / height));
}

/** Inches above the collar origin where the front collar band crosses the label. */
export const TAG_COLLAR_CUT_INCHES = 0.7;

/**
 * First guess at the NECK OPENING, mirroring MockupGeometry::defaultNeckClip.
 * The renderer KEEPS what falls inside it — the label is printed inside the
 * garment, so the shirt blocks everything outside the opening.
 */
export function defaultNeckClip(ppi, horizontalCenter, collarOffset) {
  const top = collarOffset - 2.8 * ppi;
  const bottom = collarOffset - TAG_COLLAR_CUT_INCHES * ppi;
  const h = bottom - top;
  return {
    x: Math.round(horizontalCenter),
    y: Math.round(top + h / 2),
    w: Math.round(2.6 * ppi),
    h: Math.round(h),
  };
}
